/**
 * ── THE PROJECT NAME SUGGESTION (2026-09-10) ──────────────────────────────────────
 *
 * Port of the monolith's pnSuggest ("Geo + Promotion + Eblast for M/D"), moved out of
 * FormView so it can be tested, and changed on two points Jack raised:
 *
 *   "for the project name need to take into account email nurture template as well
 *    as community — if we select a community should be in the project"
 *
 * 1. THE COMMUNITY LEADS WHENEVER ONE IS PICKED, whatever the focus says. The old
 *    rule only read `community` under a Community focus; a community chosen on a
 *    Market or Division request never reached the name.
 * 2. THE TEMPLATE IS THE PROMOTION. Measured on prod: division ATL, initiative
 *    "3357 - Coming Soon for Arlington Heights", template Now Selling - Just Launched
 *    → the name read "Atlanta Coming Soon Eblast" — the initiative's words, not the
 *    email's. The template names what the email IS, so when one is picked it takes the
 *    promotion slot; the initiative's promotion is the fallback for a request with no
 *    template yet (and stays out when it would only repeat the template or the geo).
 *
 * Geo order: community → masterplan → area of interest → the one market (by name)
 * → the division. Never the raw code.
 */
export interface ProjectNameInput {
  focus?: string;
  community?: string;
  masterplan?: string;
  areaOfInterest?: string;
  /** "ATL — Atlanta" style label, or the bare code */
  divisionLabel?: string;
  /** the selected market codes, with names when known */
  markets?: { code: string; name?: string }[];
  initiative?: string;
  /** the nurture template label ("Now Selling - Just Launched") */
  template?: string;
  /** yyyy-mm-dd */
  targetDueDate?: string;
  /** page-2 Target Deployment Date/Time — beats the page-1 due date when set (Katie, 2026-09-10) */
  targetDeploy?: string;
}

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const clean = (s: unknown) => String(s || "").replace(/\([^)]*\)/g, "").replace(/\s+/g, " ").trim();
const first = (csv: unknown) => String(csv || "").split(",")[0].trim();

/** the initiative's promotion words: "3357 - Coming Soon for Arlington Heights - 9/1/2026 - 4/30/2027" → "Coming Soon" */
export function initiativePromo(init: unknown): string {
  return String(init || "")
    .replace(/^\s*\d+\s*[-–—]\s*/, "")
    .replace(/\s+for\s+.+$/i, "")
    .replace(/\s*[-–—]\s*Ongoing\s*$/i, "")
    .replace(/\s*[-–—]\s*\d{1,2}\/\d{1,2}\/\d{2,4}.*$/, "")
    .trim();
}

/**
 * The community an initiative NAMES — "470 - Evergreen Marketing for Riverstone - Ongoing"
 * → "Riverstone" (Katie, Form Feedback 2026-09-10: the name should carry the initiative's
 * community when the form itself has not picked one). "" when the initiative has no "for".
 */
export function initiativeSubject(initiative: unknown): string {
  const s = String(initiative || "")
    .replace(/\s*[-–—]\s*Ongoing\s*$/i, "")
    .replace(/\s*[-–—]\s*\d{1,2}\/\d{1,2}\/\d{2,4}.*$/, "");
  const m = /\bfor\s+(.+?)\s*$/i.exec(s);
  return m ? clean(m[1]) : "";
}

/** "2026-10-06 10:00" / "2026-10-06T10:00" / "2026-10-06" → "10/6"; anything else → "" */
export function shortDate(d: unknown): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d || "").trim());
  return m ? +m[2] + "/" + +m[3] : "";
}

export function suggestProjectName(f: ProjectNameInput): string {
  /* 1 · geo — the most specific place the request names */
  let geo = first(f.community) || first(f.masterplan) || String(f.areaOfInterest || "").trim();
  if (!geo && (f.markets || []).length === 1) geo = String(f.markets![0].name || "").trim();
  /* the community the initiative names, before the division (Katie, 2026-09-10) */
  if (!geo) geo = initiativeSubject(f.initiative);
  if (!geo) {
    const txt = String(f.divisionLabel || "");
    geo = (txt.split("—")[1] || txt.split(" - ")[1] || txt || "").trim();
  }
  geo = clean(geo);
  /* no place yet → no suggestion. The template has a default from the first render,
     so without this a blank form would name itself "Coming Soon Eblast" — and save
     that over a stored draft (App.draft.test). A name needs a place before it is one. */
  if (!geo) return "";

  /* 2 · promotion — the template when there is one, else the initiative */
  /* the "ECH - " prefix (Layne's 2026-10-19 rename) is a library label, not words a
     project name should carry — "Granary Park Available Now Eblast", not "…ECH - Available…" */
  const tpl = clean(f.template).replace(/^ECH\s*-\s*/i, "").trim();
  let promo = tpl || initiativePromo(f.initiative);
  if (promo && geo && promo.toLowerCase().indexOf(geo.toLowerCase()) >= 0) {
    promo = promo.replace(new RegExp(esc(geo), "i"), "").replace(/\s+/g, " ").trim();
  }

  /* 3 · when */
  /* the page-2 Target Deployment Date beats the page-1 Target Due Date (Katie, 2026-09-10) */
  const when = shortDate(f.targetDeploy) || shortDate(f.targetDueDate);

  let name = [geo, promo, "Eblast"].filter(Boolean).join(" ") + (when ? " for " + when : "");
  name = name.replace(/\s+/g, " ").trim();
  return name === "Eblast" ? "" : name;
}
